import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DATE_LOCALES,
  dictionaries,
  Lang,
  SERVICE_IDS,
} from "./translations";

const STORAGE_KEY = "pontepie_lang";

type TranslateVars = Record<string, string | number>;

type I18nValue = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (path: string, vars?: TranslateVars) => string;
  dateLocale: string;
  serviceLabel: (id: string, fallback?: string) => string;
  statusLabel: (status: string) => string;
};

const I18nContext = createContext<I18nValue | undefined>(undefined);

function readPath(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in (acc as object)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function applyVars(text: string, vars?: TranslateVars): string {
  if (!vars) return text;
  return Object.entries(vars).reduce(
    (out, [key, value]) => out.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    text
  );
}

function isLang(value: string | null): value is Lang {
  return value === "es" || value === "gl" || value === "en";
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isLang(stored)) return stored;
    } catch {
      /* ignore */
    }
    return "es";
  });

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (path: string, vars?: TranslateVars) => {
      const fromLang = readPath(dictionaries[lang], path);
      const fromEs = readPath(dictionaries.es, path);
      const raw = typeof fromLang === "string" ? fromLang : typeof fromEs === "string" ? fromEs : path;
      return applyVars(raw, vars);
    },
    [lang]
  );

  useEffect(() => {
    document.documentElement.lang = lang;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("meta.description"));
  }, [lang, t]);

  const serviceLabel = useCallback(
    (id: string, fallback?: string) => {
      const key = SERVICE_IDS[id as keyof typeof SERVICE_IDS];
      if (key) return t(`services.${key}.title`);
      return fallback || id;
    },
    [t]
  );

  const statusLabel = useCallback(
    (status: string) => {
      const mapped = t(`status.${status}`);
      return mapped.startsWith("status.") ? status : mapped;
    },
    [t]
  );

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang,
      t,
      dateLocale: DATE_LOCALES[lang],
      serviceLabel,
      statusLabel,
    }),
    [lang, setLang, t, serviceLabel, statusLabel]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n debe usarse dentro de LanguageProvider");
  return ctx;
}
